// The following code is exported from marqy@v0.0.8 and refactored
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

interface MarqyProps {
  speed?: number
  direction?: 'left' | 'right'
  pauseOnHover?: boolean
  children: ReactNode
}

function useWidth() {
  const [width, setWidth] = useState(0)
  const [node, setNode] = useState<HTMLElement | null>(null)
  const observer = useRef<ResizeObserver | null>(null)

  const observe = useCallback(() => {
    observer.current = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    )
    if (node) observer.current.observe(node)
  }, [node])
  const disconnect = useCallback(() => {
    observer.current?.disconnect()
  }, [])

  useEffect(() => {
    observe()
    return () => disconnect()
  }, [disconnect, observe])

  return [setNode, width] as const
}

export const Marqy = ({
  speed = 0.5,
  direction = 'left',
  pauseOnHover,
  children,
  ...rest
}: MarqyProps): JSX.Element => {
  const [reps, setReps] = useState(1)

  const [container, containerWidth] = useWidth()
  const [item, itemWidth] = useWidth()

  useEffect(() => {
    if (containerWidth && itemWidth) {
      setReps(Math.ceil(containerWidth / itemWidth))
    }
  }, [containerWidth, itemWidth])

  return (
    <div
      ref={container}
      data-marqy=""
      data-direction={direction}
      data-pause-on-hover={pauseOnHover ? '' : null}
      {...rest}
    >
      <div data-marqy-inner="">
        {new Array(2).fill(0).map((_, clone) => {
          return (
            <div
              key={clone}
              data-marqy-content=""
              style={{
                animationDuration: `${
                  ((itemWidth ?? 0) * reps) / (100 * speed)
                }s`,
              }}
            >
              {new Array(reps).fill(0).map((_, rep) => {
                const isFirstItem = clone === 0 && rep === 0
                return (
                  <div
                    key={rep}
                    ref={isFirstItem ? item : undefined}
                    aria-hidden={!isFirstItem || undefined}
                    data-marqy-item=""
                  >
                    {children}
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}
